import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Monitor, Smartphone } from "lucide-react";
import { formatInt } from "@/lib/format";
import { callOp, hasOp } from "@/lib/stubs";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmailPreviewPanel, Frame } from "@/features/email/EmailPreviewPanel";

/** crm-billing's thank-you page preview (sample buyer and order). */
export interface ThankYouPreview {
  /** Which page a buyer gets: the product's CRM page, its site thank_you_url, or the receipt page. */
  source: "crm_page" | "site_url" | "receipt" | string;
  /** Self-contained page (inline styles, no scripts); shown in a sandboxed frame. */
  html?: string | null;
  url?: string | null;
  ads_send_to?: string | null;
}

/** crm-billing's purchase email preview, merged with sample data. */
export interface PurchaseEmailPreview {
  source: "product" | "generic_receipt" | string;
  subject: string;
  preheader?: string | null;
  html: string;
  brand_id: number;
  sender?: string | null;
}

/** crm-automations' automations_for_product row (GET /api/v1/products/{product_id}/automations). */
export interface ProductAutomation {
  id: number;
  name: string;
  /** draft (never published), shadow, active, paused, archived */
  status: string;
  /** on = active, shadow = logs only, off = draft, paused or archived */
  state: "on" | "shadow" | "off" | string;
  brand_id?: number | null;
  /** product = a trigger names this product; brand = any product of its brand; any = any product at all */
  match: "product" | "brand" | "any" | string;
  /** Only the unpublished draft has the trigger. */
  draft_only?: boolean;
  triggers?: { key: string; type: string; match: string; says: string }[];
  /** People in it now (active, waiting or paused runs). */
  live_count?: number | null;
  updated_at?: string;
}

const OPS = { page: "products_preview_thank_you", email: "products_preview_purchase_email", autos: "automations_for_product" } as const;

const useHas = (op: string) => useQuery({ queryKey: ["has-op", op], queryFn: () => hasOp(op), staleTime: Infinity }).data === true;

/**
 * "After purchase": what a buyer of this product sees and gets, view only (Brandon, 2026-10-27: content is kept in
 * the CRM and edited through Claude, previewable here). Each tab appears once its stream's operation exists.
 */
export function AfterPurchaseCard({ productId }: { productId: number }) {
  const hasPage = useHas(OPS.page);
  const hasEmail = useHas(OPS.email);
  const hasAutos = useHas(OPS.autos);
  const tabs = [hasPage && "page", hasEmail && "email", hasAutos && "automations"].filter(Boolean) as string[];
  const [tab, setTab] = useState<string | null>(null);
  if (tabs.length === 0) return null;
  const current = tab && tabs.includes(tab) ? tab : tabs[0];
  return (
    <Card>
      <CardHeader>
        <CardTitle>After purchase: what buyers get</CardTitle>
        <span className="text-xs text-[var(--color-muted-foreground)]">View only. Claude changes these (MCP).</span>
      </CardHeader>
      <CardContent>
        <Tabs value={current} onValueChange={setTab}>
          <TabsList>
            {hasPage && <TabsTrigger value="page">Thank-you page</TabsTrigger>}
            {hasEmail && <TabsTrigger value="email">Purchase email</TabsTrigger>}
            {hasAutos && <TabsTrigger value="automations">Automations</TabsTrigger>}
          </TabsList>
          {hasPage && (
            <TabsContent value="page" className="pt-3">
              <ThankYouTab productId={productId} />
            </TabsContent>
          )}
          {hasEmail && (
            <TabsContent value="email" className="pt-3">
              <EmailTab productId={productId} />
            </TabsContent>
          )}
          {hasAutos && (
            <TabsContent value="automations" className="pt-3">
              <AutomationsTab productId={productId} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

const PAGE_WIDTHS = [
  { key: "phone", label: "Phone", px: 390, icon: Smartphone },
  { key: "desktop", label: "Desktop", px: 1024, icon: Monitor },
] as const;

function ThankYouTab({ productId }: { productId: number }) {
  const q = useQuery({
    queryKey: ["product-thank-you-preview", productId],
    queryFn: () => callOp<ThankYouPreview>(OPS.page, { product_id: productId }),
    retry: false,
  });
  const [width, setWidth] = useState<"phone" | "desktop">("phone");
  if (q.isLoading) return <Skeleton className="h-40" />;
  if (q.error) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>;
  const p = q.data!;
  const w = PAGE_WIDTHS.find((x) => x.key === width)!;
  return (
    <div className="space-y-3 text-sm">
      <p>
        <span className="text-[var(--color-muted-foreground)]">Buyers see: </span>
        {p.source === "crm_page" && "this page, right after paying (sample buyer and order below)."}
        {p.source === "site_url" && (
          <>
            the site's thank-you page{" "}
            {p.url && (
              <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 break-all text-[var(--color-primary)] hover:underline">
                {p.url}
                <ExternalLink className="size-3 shrink-0" aria-hidden />
              </a>
            )}
            . No CRM page is set for this product.
          </>
        )}
        {p.source === "receipt" && "the CRM receipt page. Neither a CRM thank-you page nor a site page is set."}
        {!["crm_page", "site_url", "receipt"].includes(p.source) && p.source}
      </p>
      <p className="text-xs text-[var(--color-muted-foreground)]">
        {p.ads_send_to ? (
          <>
            Google Ads conversion <span className="num">{p.ads_send_to}</span>: the real page reports it once per order, with the order total. This preview never
            sends it.
          </>
        ) : (
          "No Google Ads conversion is set for this product."
        )}
      </p>
      {p.html ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="muted">sample data</Badge>
            <span className="ml-auto inline-flex rounded-md border p-0.5" role="group" aria-label="Preview width">
              {PAGE_WIDTHS.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  aria-pressed={width === x.key}
                  onClick={() => setWidth(x.key)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded px-2 py-1 text-xs",
                    width === x.key ? "bg-[var(--color-muted)] font-medium" : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
                  )}
                >
                  <x.icon className="size-3.5" aria-hidden /> {x.label}
                </button>
              ))}
            </span>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">The page follows each visitor's light or dark setting, like the brand sites.</p>
          <div className={cn("grid gap-3", width === "phone" ? "max-w-[796px] sm:grid-cols-2" : "lg:grid-cols-2")}>
            <Frame doc={p.html} width={w.px} label={`Light, ${w.label.toLowerCase()} (${w.px} px)`} height={width === "phone" ? 760 : 700} scheme="light" />
            <Frame doc={p.html} width={w.px} label={`Dark, ${w.label.toLowerCase()} (${w.px} px)`} height={width === "phone" ? 760 : 700} scheme="dark" dark />
          </div>
        </>
      ) : null}
    </div>
  );
}

function EmailTab({ productId }: { productId: number }) {
  const q = useQuery({
    queryKey: ["product-purchase-email-preview", productId],
    queryFn: () => callOp<PurchaseEmailPreview>(OPS.email, { product_id: productId }),
    retry: false,
  });
  if (q.isLoading) return <Skeleton className="h-40" />;
  if (q.error) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>;
  const e = q.data!;
  return (
    <div className="space-y-3 text-sm">
      <p>
        {e.source === "product"
          ? "This product's own purchase email. It replaces the generic receipt for these orders (it carries the receipt link)."
          : "No purchase email of its own: buyers get billing's generic receipt, shown here."}
      </p>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1">
        <dt className="text-[var(--color-muted-foreground)]">Subject</dt>
        <dd className="font-medium">{e.subject}</dd>
        {e.preheader && (
          <>
            <dt className="text-[var(--color-muted-foreground)]">Preview text</dt>
            <dd>{e.preheader}</dd>
          </>
        )}
        {e.sender && (
          <>
            <dt className="text-[var(--color-muted-foreground)]">From</dt>
            <dd className="break-all">{e.sender}</dd>
          </>
        )}
      </dl>
      <EmailPreviewPanel html={e.html} brandId={e.brand_id} subject={e.subject} preheader={e.preheader ?? undefined} kind="transactional" />
    </div>
  );
}

const STATE_CHIP: Record<string, "success" | "muted" | "warning"> = { on: "success", shadow: "muted", off: "muted" };

const MATCH_LABEL: Record<string, string> = { product: "names this product", brand: "any product of the brand", any: "any product" };

function AutomationsTab({ productId }: { productId: number }) {
  const q = useQuery({
    queryKey: ["automations-for-product", productId],
    queryFn: () => callOp<{ automations: ProductAutomation[] }>(OPS.autos, { product_id: productId }),
    retry: false,
  });
  if (q.isLoading) return <Skeleton className="h-24" />;
  if (q.error) return <p className="text-sm text-[var(--color-danger-fg)]">{q.error.message}</p>;
  const rows = q.data?.automations ?? [];
  if (rows.length === 0)
    return (
      <p className="text-sm text-[var(--color-muted-foreground)]">
        No automation starts when someone buys this. To link one, Claude adds this product to an automation's purchase or subscription trigger.
      </p>
    );
  return (
    <ul className="divide-y text-sm">
      {rows.map((a) => {
        // "off" says why: draft, paused or archived.
        const label = a.state === "off" ? (a.status === "draft" ? "off (draft)" : `off (${a.status})`) : a.state;
        return (
          <li key={a.id} className="space-y-0.5 py-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to="/automations/$automationId" params={{ automationId: String(a.id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                {a.name}
              </Link>
              {a.brand_id ? <BrandMark id={a.brand_id} /> : null}
              <span className="flex gap-1">
                <Badge variant={STATE_CHIP[a.state] ?? "muted"} title={a.state === "shadow" ? "Shadow: logs what it would do, sends nothing" : undefined}>
                  {label}
                </Badge>
                {a.match !== "product" && <Badge variant="outline">{MATCH_LABEL[a.match] ?? a.match}</Badge>}
                {a.draft_only && a.status !== "draft" && (
                  <Badge variant="warning" title="Only the unpublished draft has this trigger; the live version doesn't start on this product yet">
                    draft only
                  </Badge>
                )}
              </span>
              {a.live_count != null && <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">{formatInt(a.live_count)} in it now</span>}
            </div>
            {(a.triggers ?? []).map((t) => (
              <div key={t.key} className="text-xs text-[var(--color-muted-foreground)]">
                {t.says}
              </div>
            ))}
          </li>
        );
      })}
    </ul>
  );
}
